import { Component, OnInit, OnDestroy, Input, OnChanges } from '@angular/core';
import { ReadabilityRepoService } from '@client/app/services/api/readability-repo/readability-repo.service';
import { QAReadabilityService } from '@client/app/blocks/service/qa-readability/qareadability.service';
import { ENTITY_CONSTANT } from '@client/app/core/constants/entity.constant';
import { BaseEntityService } from '@client/app/blocks/base/base.entity.service';
import { LabelService } from '@monsido/services/label-service/label.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { BaseContainerComponentController } from '@client/app/blocks/base/base.controller';

@Component({
    selector: 'readability-pages-with-score',
    standalone: false,
    template: `
        <div class="readability-pages-with-score">
            <div class="mon-dialog-header">
                @let readabilityScore = (score | translateReadability:domain.features.readability_test);
                <page-header
                        [subHeader]="pages.total + ' ' + ('page found' | translateWithPlural:pages.total:'pages found')"
                        [header]="('Pages with score' | translate) + ': ' + readabilityScore"
                        [subHeaderTooltipEnable]="false"
                        [panelLeftClass]="'col-32 col-md-28 col-lg-30'"
                        [panelRightClass]="'col-16 col-md-20 col-lg-18'">
                    <page-header-content>
                        <div class="row mon-dialog-delay-fadein">
                            <div class="col-auto ml-auto pt-1 pr-1">
                                <csv-export-button clsSize="py-2 px-3"
                                                   clsMargin="mr-0"
                                                   [exports]="exports">
                                </csv-export-button>
                            </div>

                            <div class="col pr-6 mt-1">
                                <table-search [minSearchLength]="3"
                                              class="block mr-2"
                                              theme="grey-theme"
                                              [placeholderTxt]="'Search...' | translate"
                                              [model]="search"
                                              (getPage)="onSearch($event)">
                                </table-search>
                            </div>
                        </div>
                    </page-header-content>
                </page-header>
            </div>
            <div class="mon-dialog-body">
                <div class="row merge-to-page-header px-5 align-items-stretch">
                    <div class="col-lg-24 border-r-md">
                        <div class="py-3 border-b-md">
                            <strong translate>Quick help</strong><br/>
                            <span translate>Put simply, readability is the ease with which a reader can understand
                            the written text. Readability tests, readability formulas, or readability metrics are
                            formulae for evaluating the readability of text, by counting syllables, words, and sentences.
                            Scores are compared with scales based on judged linguistic difficulty or reading grade level.</span>
                        </div>
                    </div>
                    <div class="col-lg-24 merge-l-border py-3">
                        <strong translate>Pages with score:</strong> {{ score | translateReadability:domain.features.readability_test }}<br/>
                        <ngb-progressbar class="flat" [value]="total" [max]="totalPages" type="marine-blue"></ngb-progressbar>
                        <div class="pb-3 d-flex">
                            <div class="mr-auto">
                                {{ total }}
                                <span translate [translate-args]="{total: total}" [translate-plural]="'pages'">page</span>
                                ({{ totalPercentage }} %)
                            </div>
                            <div>
                                {{ totalPages - total }}
                                <span translate [translate-args]="{remaningPages: totalPages - total}" [translate-plural]="'pages'">page</span>
                                ({{ remaningPagesPercentage }} %)
                            </div>
                        </div>
                    </div>
                </div>
                <div class="card panel-monsido-card mx-4">
                    <div class="card-body">
                        <table-container [collection]="pages"
                                         (onPageChange)="onPageChange($event)"
                                         (onPerPageChange)="onPageSizeChange($event)"
                                         [progress]="progress">
                            <table class="table table-hover table-fixed table-with-labels">
                                <thead>
                                    <tr>
                                        <th monTableSort
                                            [monSortBy]="'title'"
                                            [monSortDirection]="currentSort.direction"
                                            [monCurrentlySortedBy]="currentSort.by"
                                            (monOnSorted)="onMonTableSort($event)">
                                            {{ 'Title and URL' | translate }}
                                        </th>
                                        <th class="col-7 text-right col-sm-4 col-md-4 col-lg-4"
                                            monTableSort
                                            [monSortBy]="'readability'"
                                            [monSortDirection]="currentSort.direction"
                                            [monCurrentlySortedBy]="currentSort.by"
                                            (monOnSorted)="onMonTableSort($event)">
                                            {{ 'Readability Score' | translate }}
                                        </th>
                                        <th class="col-3 text-right" translate>Total words</th>
                                        <th monTableSort
                                            [monSortBy]="'priority_score'"
                                            [monSortDirection]="currentSort.direction"
                                            [monCurrentlySortedBy]="currentSort.by"
                                            (monOnSorted)="onMonTableSort($event)"
                                            class="text-right col-sm-4 col-md-4 col-lg-4">
                                            {{ 'Priority' | translate }}
                                        </th>
                                        <th class="col-sm-5 col-md-5 col-lg-4 text-right"
                                            monTableSort
                                            [monSortBy]="'hits'"
                                            [monSortDirection]="currentSort.direction"
                                            [monCurrentlySortedBy]="currentSort.by"
                                            (monOnSorted)="onMonTableSort($event)"
                                            *ngIf="viewsColumIsAvailable">
                                            <span translate>Views</span>
                                            <span class="outer-l-smurf fas fa-question-circle"
                                                  aria-hidden="true"
                                                  ngbTooltip="{{ 'Total page views over the last 30 days' | translate }}"
                                                  [triggers]="'focus hover'">
                                            </span>
                                        </th>
                                        <th class="col-6">&nbsp;</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr *ngFor="let page of pages">
                                        <td>
                                            <div class="text-ellipsis">
                                                <mon-url-title
                                                        *ngIf="!pageDetailsIsAvailable"
                                                        [title]="page.title"
                                                        [url]="page.url"
                                                        [active]="false">
                                                </mon-url-title>
                                                <url-title-page-details
                                                        *ngIf="pageDetailsIsAvailable"
                                                        (onClose)="onPageDetailsClose()"
                                                        [page]="page"
                                                        [title]="page.title"
                                                        [url]="page.url"
                                                        [active]="true">
                                                </url-title-page-details>
                                            </div>
                                            <label-table *ngIf="showLabels"
                                                             [assignedLabels]="page.labels"
                                                             [existingLabels]="labels"
                                                             (getLabels)="getLabels()"
                                                             (onUpdateLabel)="updateLabels(page)" />
                                        </td>
                                        <td class="text-v-middle text-right">
                                            <span class="fa-mon-circle fa-lg mr-1">
                                                {{ (page.readability || 0) | monNumber }}
                                            </span>
                                            <ng-container *ngIf="page.readability_level !== 'na'">
                                                {{page.readability_level | translateReadability:domain.features.readability_test}}
                                            </ng-container>
                                            <ng-container *ngIf="page.readability_level === 'na'">
                                                {{(page.readability_reason || page.readability_level) |
                                                translateReadability:domain.features.readability_test}}
                                            </ng-container>
                                        </td>
                                        <td class="text-v-middle text-right">
                                            {{ page.readability_words_count | monNumber }}
                                        </td>
                                        <td class="text-v-middle text-right">
                                            <mon-priority class="vcenter" [priority]="page.priority_score_as_text"></mon-priority>
                                        </td>
                                        <td *ngIf="viewsColumIsAvailable">
                                            <visits [title]="page.title" [visits]="page.hits"
                                                    [totalVisits]="pages.pageHits"></visits>
                                        </td>
                                        <td class="text-v-middle">
                                            <div class="d-flex align-items-center justify-content-end flex-wrap flex-md-nowrap">
                                                <table-page-details
                                                        *ngIf="page.type === 'html'"
                                                        ngbTooltip="{{'Open Page Details' | translate}}"
                                                        [triggers]="'focus hover'"
                                                        [target]="'page-details-section-qa'"
                                                        [subTarget]="'readability'"
                                                        [page]="page">
                                                </table-page-details>
                                                <table-document-details
                                                        *ngIf="page.type !== 'html'"
                                                        [attr.aria-label]="'Document Details' | translate"
                                                        ngbTooltip="{{'Open Document Details' | translate}}"
                                                        [triggers]="'focus hover'"
                                                        [document]="page"
                                                        target="page-details-section-qa"
                                                        [subTarget]="'readability'"
                                                        (callBack)="onPageDetailsClose()">
                                                </table-document-details>
                                                <mon-extension-visual-link-button class="ml-1" [page]="page"
                                                [type]="'rdblty'"></mon-extension-visual-link-button>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </table-container>
                    </div>
                </div>
            </div>
        </div>
    `,
})
export class ReadabilityPagesWithScoreComponent extends BaseContainerComponentController implements OnInit, OnChanges, OnDestroy {
    @Input() score: any;
    @Input() filterType: any;

    pages: any = [];
    total: number = 0;
    totalPages: number = 0;
    totalPercentage: string = '0';
    remaningPagesPercentage: string = '0';
    progress: any = null;
    viewsColumIsAvailable: boolean = false;
    pageDetailsIsAvailable: boolean = false;
    exports: any[] = [];
    domain: any = {};
    eventId: any;

    constructor (
        private readabilityRepoService: ReadabilityRepoService,
        private qaReadabilityService: QAReadabilityService,
        private baseEntityService: BaseEntityService,
        private labelService: LabelService,
        private activeFeatureService: ActiveFeatureService,
    ) {
        super({ table: true, labels: true, pages: true });
    }

    ngOnDestroy () {
        if (this.eventId) {
            this.monEventsService.remove(this.eventId);
        }
    }

    ngOnInit () {
        this.eventId = this.monEventsService.addListener(
            ENTITY_CONSTANT.PAGE_UPDATED_EVENT as any,
            this.baseEntityService.updateEntryInArray(this, 'pages', 'id'),
        );

        this.domain = this.sessionService.domain;
        this.pages = [];
        this.total = 0;
        const crawlHistory = this.sessionService.domain?.crawl_history;
        this.totalPages = (crawlHistory?.page_count || 0) + (crawlHistory?.documents_count || 0);
        this.totalPercentage = '0';
        this.remaningPagesPercentage = '0';
        this.currentSort = { by: 'priority_score', direction: 'desc' };
        this.progress = null;
        this.search = '';
        this.viewsColumIsAvailable = this.activeFeatureService.isFeatureActive('script_setup_guide');
        this.pageDetailsIsAvailable = this.activeFeatureService.isFeatureActive('page_details');
        this.exports = [
            {
                data: {
                    category: 'readability_by_score',
                    category_ref: this.getReaderLevelName(this.score),
                },
            },
        ];

        // Watch for score changes
        this.watchScoreChanges();
        this.getPage();
        this.getLabels();
    }

    private watchScoreChanges () {
        // In Angular, we can watch for input changes using ngOnChanges or manually check
        // For now, we'll call getPage when score changes
        if (this.score !== null && this.score !== undefined) {
            this.getPage();
        }
    }

    ngOnChanges () {
        this.watchScoreChanges();
    }

    override getPage () {
        const params = {
            readability_level: this.score,
            page: this.page,
            search: this.search || '',
            page_size: this.pageSize,
            sort_by: this.currentSort.by,
            sort_dir: this.currentSort.direction,
            type: this.filterType,
        };

        this.progress = this.readabilityRepoService.pages(params).then((data: any) => {
            this.pages = data;
            this.total = data.total;
            this.totalPercentage = this.pipeService.get('monNumber')((this.total / this.totalPages) * 100, '0.[00]');
            this.remaningPagesPercentage = this.pipeService.get('monNumber')(((this.totalPages - this.total) / this.totalPages) * 100, '0.[00]');
        }, () => {});
    }

    private getReaderLevelName (score: any): string {
        return this.qaReadabilityService.getReaderLevelName(score);
    }

    createOrUpdateLabel (label: any) {
        this.labelService.createOrUpdateLabel(label);
    }
}
